import { useEffect } from 'react';
import PageMeta from '@/components/PageMeta';
import Layout from '@/components/layout/Layout';
import HeroSection from '@/components/home/HeroSection';
import ScrollRevealWrapper from '@/components/home/ScrollRevealWrapper';
import { prefetchTopRoutes } from '@/utils/route-prefetch';

// Eager imports for the home page sections.
//
// Previously these were wrapped in `lazyWithRetry()` to defer below-the-fold
// JS, but `React.lazy` triggers dev-mode `validateFunctionComponentInDev`
// ref checks on every wrapped component, producing noisy "Function components
// cannot be given refs" warnings even though no caller actually passes a ref.
// The Index route already lives behind the App-level route `lazy()` boundary,
// so these section modules ship in the same chunk as `Index` and bundle size
// is unchanged. Eager-loading also removes the Suspense boundary, eliminates
// a render-tier flicker on slow connections, and matches how the home page
// is actually consumed (all sections are visible after one short scroll).
import BentoExplore from '@/components/home/BentoExplore';
import StatsStrip from '@/components/home/StatsStrip';
import StewardshipSection from '@/components/home/StewardshipSection';
import FeaturedExhibitSection from '@/components/home/FeaturedExhibitSection';
import AppointmentCTA from '@/components/home/AppointmentCTA';

const museumJsonLd = {
  "@context": "https://schema.org",
  "@type": "Museum",
  "name": "Y.O. Ranch Headquarters Texas History Museum",
  "description": "A private, appointment-only museum preserving Texas Ranger heritage, Hill Country history, and the legacy of the Y.O. Ranch. Available for registered guests only.",
  "url": "https://yoranch.lovable.app",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Mountain Home",
    "addressRegion": "TX",
    "addressCountry": "US"
  },
  "openingHours": "By Appointment Only",
  "isAccessibleForFree": false,
  "publicAccess": false,
  "sameAs": [
    "https://yoranchheadquarters.com"
  ],
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Museum Experiences",
    "itemListElement": [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Private Museum Tour",
          "description": "Curator-led private tours for registered Y.O. Ranch Headquarters guests"
        }
      }
    ]
  }
};

const Index = () => {
  useEffect(() => {
    prefetchTopRoutes();
  }, []);

  return (
    <>
      <PageMeta
        title="Y.O. Ranch Headquarters Texas History Museum | Mountain Home, TX"
        titleOverride
        description="Discover Texas Ranger heritage, frontier firearms, and 150 years of Hill Country history at the Y.O. Ranch Headquarters Museum. Private tours by appointment for registered guests."
        canonicalPath="/"
        ogType="museum"
        jsonLd={museumJsonLd}
      >
        <meta name="keywords" content="Y.O. Ranch, Texas history museum, Texas Rangers, Schreiner family, Hill Country, Mountain Home TX, Texas Longhorns, frontier history" />
      </PageMeta>
      <Layout>
        <HeroSection />
        <ScrollRevealWrapper>
          <BentoExplore />
        </ScrollRevealWrapper>
        <ScrollRevealWrapper>
          <StatsStrip />
        </ScrollRevealWrapper>
        <ScrollRevealWrapper>
          <StewardshipSection />
        </ScrollRevealWrapper>
        <ScrollRevealWrapper>
          <FeaturedExhibitSection />
        </ScrollRevealWrapper>
        <ScrollRevealWrapper>
          <AppointmentCTA />
        </ScrollRevealWrapper>
      </Layout>
    </>
  );
};

export default Index;
